import React, { PropsWithChildren } from "react";
import { useMyAccessProfileV2 } from "../apis/tryst/get-my-access-profile";
import CustomisationTemplate from "../organisms/receipt-customisation/customisation-template";
import ReceiptPdf from "../organisms/receipt-customisation/receipt-pdf";
import DoctorsList from "../organisms/queue/doctor-filter/doctor-list";
import { RendererParams } from "../react-app-env";
import useReceiptCustomisationStore from "../stores/receipt-customisation/receipt-customisation-store";
import getBridges from "../utils/bridges/get-bridges";
import { docListMassaged } from "../organisms/receipt-customisation/customisation-utls";

const ReceiptCustomisation = (
  props: PropsWithChildren<Partial<RendererParams>>
) => {
  const { routeParams } = props;
  const { cId } = routeParams || {};

  // Move all hooks to the top, before any conditional logic
  const { navigate, changeClinic } = getBridges() || {};
  const { setClinicId, setSelectedDoctorId } = useReceiptCustomisationStore();
  const { data: myAccessProfile } = useMyAccessProfileV2();

  // Make sure docListMassaged() doesn't contain hooks
  const actualArray = React.useMemo(() => docListMassaged(), []);

  const [selectedDoctor, setSelectedDoctor] = React.useState<any>(
    actualArray?.doctors?.[0]
  );

  // Extract the value to avoid deep property access in dependencies
  const defaultClinic = myAccessProfile?.doctors?.find(t => t.oid === selectedDoctor.oid)?.default_clinic;

  React.useEffect(() => {
    if (!!cId) {
      setClinicId({ clinicId: cId });
      return;
    }

    let defaultClinicId;

    if (defaultClinic) {
      defaultClinicId = defaultClinic;
    } else if (actualArray?.doctors?.[0]?.clinics?.[0]?.id) {
      defaultClinicId = actualArray.doctors[0].clinics[0].id;
    }

    if (defaultClinicId) {
      setClinicId({ clinicId: defaultClinicId });
    }
  }, [cId, defaultClinic, setClinicId, actualArray?.doctors]);

  // Initialize selectedDoctorId on mount if not set
  React.useEffect(() => {
    if (selectedDoctor?.oid) {
      setSelectedDoctorId({ doctorId: selectedDoctor.oid });
    } else if (myAccessProfile?.loginmeta?.docid) {
      setSelectedDoctorId({ doctorId: myAccessProfile.loginmeta.docid });
    }
  }, [
    selectedDoctor?.oid,
    setSelectedDoctorId,
    myAccessProfile?.loginmeta?.docid,
  ]);

  const handleDoctorSelect = (doctor: any) => {
    setSelectedDoctor(doctor);

    // Reset hasChanged to false when changing doctor to allow config reload
    setSelectedDoctorId({ doctorId: doctor.oid });

    const firstClinic = doctor.clinics?.[0];
    if (firstClinic) {
      setClinicId({ clinicId: firstClinic.id });
    }

    if (!cId && firstClinic) {
      changeClinic?.(firstClinic.id);
      navigate?.(
        "replace",
        `/app/receipt-config/${firstClinic.id}?doctorId=${doctor.oid}`
      );
    }
  };

  return (
    <>
      {actualArray?.doctors?.length > 1 ? (
        <div className="dr-space-x-4 dr-z-1 dr-pt-16 dr-pb-8 dr-pl-16 dr-flex dr-flex-row dr-overflow-x-scroll hide-scrollbar dr-relative w-full bg-neutral-0 ">
          <DoctorsList
            onSelectDoctorClick={handleDoctorSelect}
            doctorsList={actualArray?.doctors || []}
            selectedDoctorId={selectedDoctor?.oid} // Pass the selected doctor ID
          />
        </div>
      ) : null}
      <div className="dr-h-screen dr-w-full bg-neutral-8 font-lato dr-pr-16">
        <div className="dr-grid dr-grid-cols-12 dr-h-[calc(100vh-500px)]">
          <div
            className={
              actualArray?.doctors?.length <= 1
                ? "dr-col-span-7 dr-h-screen dr-pl-16 dr-py-16"
                : "dr-col-span-7 dr-h-[calc(100vh-74px)] dr-pl-16  dr-py-16"
            }
          >
            <CustomisationTemplate isInsideProfile={!cId} />
          </div>
          <div className="dr-col-span-5 dr-rounded-8 dr-w-auto dr-h-full dr-overflow-auto dr-py-12">
            <ReceiptPdf />
          </div>
        </div>
      </div>
    </>
  );
};

export default ReceiptCustomisation;
